import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {DocumentSourceTypeEnum} from 'nutshell-graphql-types';
import type {
    ToggleContextDocumentUsage as ToggleContextDocumentUsageMutation,
    ToggleContextDocumentUsageVariables as ToggleContextDocumentUsageMutationVariables,
} from 'nutshell-graphql-types';

import TOGGLE_CONTEXT_DOCUMENT_USAGE from '../mutations/toggle-context-document-usage.graphql';
import GET_CONTEXT_DOCUMENTS from '../queries/get-context-documents.graphql';

export const useToggleContextDocumentUsage = (): {
    toggleContextDocumentUsage: (input: {
        documentId: string;
        isEnabled: boolean;
        sourceType: DocumentSourceTypeEnum;
    }) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [toggleContextDocumentUsageMutation, {loading, error}] = useMutation<
        ToggleContextDocumentUsageMutation,
        ToggleContextDocumentUsageMutationVariables
    >(TOGGLE_CONTEXT_DOCUMENT_USAGE);

    return {
        toggleContextDocumentUsage: (input: {
            documentId: string;
            isEnabled: boolean;
            sourceType: DocumentSourceTypeEnum;
        }) => {
            return toggleContextDocumentUsageMutation({
                variables: {
                    input: {
                        documentId: input.documentId,
                        isEnabled: input.isEnabled,
                    },
                },
                refetchQueries: ['GetContextDocumentUsage'],
                optimisticResponse: {
                    // @ts-expect-error
                    __typename: 'Mutation',
                    contextDocumentUsageToggle: {
                        __typename: 'ContextDocument',
                        id: input.documentId,
                        isIncluded: input.isEnabled,
                        effectiveStatus: input.isEnabled ? 'SUCCESS' : 'DISABLED',
                    },
                },
                update: (proxy, {data}) => {
                    const cacheResult = proxy.readQuery({
                        query: GET_CONTEXT_DOCUMENTS,
                        variables: {
                            includeExcluded: true,
                            sourceType: input.sourceType,
                        },
                    });

                    if (
                        !cacheResult ||
                        // @ts-expect-error
                        !cacheResult.contextDocuments ||
                        !data ||
                        !data.contextDocumentUsageToggle
                    ) {
                        return;
                    }

                    proxy.writeQuery({
                        query: GET_CONTEXT_DOCUMENTS,
                        variables: {
                            includeExcluded: true,
                            sourceType: input.sourceType,
                        },
                        data: {
                            // @ts-expect-error
                            contextDocuments: cacheResult.contextDocuments.map((doc) =>
                                doc.id === input.documentId
                                    ? {
                                          ...doc,
                                          ...data.contextDocumentUsageToggle,
                                      }
                                    : doc
                            ),
                        },
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
